"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BlockListEditor = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const lucide_react_1 = require("lucide-react");
const react_1 = require("react");
const react_i18next_1 = require("react-i18next");
const yahooArticles_1 = require("../../api-clients/yahooArticles");
const ApiClient_1 = require("../../ApiClient");
const editorUtils_1 = require("./editorUtils");
const tableImage_1 = require("./tableImage");
const BLOCK_TYPES = ['Header', 'Supplement', 'P', 'Img'];
const LEVELS = ['h2', 'h3', 'h4'];
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const controlClass = 'h-[24px] rounded border border-transparent bg-transparent px-1 text-[11.5px] text-muted-foreground hover:border-border focus:border-border';
const iconButton = 'inline-flex size-[26px] items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30';
// What is missing in one block, shown under it (the full list is on the preview side).
const blockIssue = (block) => {
    if (block.type === 'Img') {
        if (!block.src)
            return 'image';
        return block.alt.trim() ? null : 'alt';
    }
    return block.text.trim() ? null : 'empty';
};
// Every image needs a title (alt); until the analyst writes one it is the next
// free 「図n」 in this section (「表n」 for a table image).
const defaultAlt = (blocks, kind) => {
    const prefix = kind === 'table' ? '表' : '図';
    const used = blocks.flatMap((b) => (b.type === 'Img' && b.alt.startsWith(prefix) ? [Number(b.alt.slice(prefix.length))] : []));
    return `${prefix}${Math.max(0, ...used.filter(Number.isInteger)) + 1}`;
};
// Uploads one image file; the error is in words for the analyst.
const useImageUpload = () => {
    const { t } = (0, react_i18next_1.useTranslation)();
    return async (file) => {
        if (!IMAGE_TYPES.includes(file.type))
            throw new Error(t('components.YahooArticles.imageType'));
        if (file.size > MAX_IMAGE_BYTES)
            throw new Error(t('components.YahooArticles.imageTooLarge'));
        try {
            const uploaded = await (0, yahooArticles_1.uploadYahooImage)(await (0, editorUtils_1.readFileAsBase64)(file));
            return { src: uploaded.key, width: uploaded.width, height: uploaded.height };
        }
        catch (err) {
            throw new Error(err instanceof ApiClient_1.ApiError ? err.message : t('components.YahooArticles.uploadFailed'));
        }
    };
};
const AutoGrow = ({ value, onChange, onPaste, onKeyDown, onDrop, inputRef, placeholder, className, disabled, }) => {
    const ref = (0, react_1.useRef)(null);
    const fit = () => {
        const el = ref.current;
        if (!el)
            return;
        el.style.height = 'auto';
        // border-box: the height includes the borders, which scrollHeight leaves out.
        el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
    };
    (0, react_1.useEffect)(fit, [value]);
    // The text re-wraps when the column gets narrower (sidebar, window) or the
    // web font replaces the fallback after the first measure; without a re-fit
    // the extra lines are cut off and a long paragraph looks like one line.
    (0, react_1.useEffect)(() => {
        const el = ref.current;
        if (!el)
            return;
        let width = el.clientWidth;
        const observer = typeof ResizeObserver === 'undefined'
            ? null
            : new ResizeObserver(() => {
                if (el.clientWidth === width)
                    return;
                width = el.clientWidth;
                // Next frame: resizing inside the callback would re-trigger it in the same frame.
                requestAnimationFrame(fit);
            });
        observer?.observe(el);
        document.fonts?.addEventListener('loadingdone', fit);
        void document.fonts?.ready.then(fit);
        return () => {
            observer?.disconnect();
            document.fonts?.removeEventListener('loadingdone', fit);
        };
    }, []);
    return ((0, jsx_runtime_1.jsx)("textarea", { ref: (el) => {
            ref.current = el;
            inputRef(el);
        }, rows: 1, value: value, disabled: disabled, placeholder: placeholder, onChange: (e) => onChange(e.target.value), onPaste: onPaste, onKeyDown: onKeyDown, onDrop: onDrop, className: `w-full resize-none overflow-hidden rounded-md border border-transparent bg-transparent px-2 py-1 leading-relaxed outline-none hover:bg-muted/40 focus:border-primary/50 focus:bg-background disabled:hover:bg-transparent ${className}` }));
};
const ImageBlock = ({ block, disabled, pending, defaultCaption, altRef, onChange, }) => {
    const { t } = (0, react_i18next_1.useTranslation)();
    const upload = useImageUpload();
    const [picking, setUploading] = (0, react_1.useState)(false);
    const uploading = picking || pending;
    const [error, setError] = (0, react_1.useState)(null);
    const [dragging, setDragging] = (0, react_1.useState)(false);
    const take = async (file) => {
        if (!file)
            return;
        setError(null);
        setUploading(true);
        try {
            const uploaded = await upload(file);
            onChange({ ...block, ...uploaded, alt: block.alt || defaultCaption });
        }
        catch (err) {
            setError(err.message);
        }
        finally {
            setUploading(false);
        }
    };
    return ((0, jsx_runtime_1.jsxs)("div", { className: "flex gap-3", children: [(0, jsx_runtime_1.jsxs)("label", { onDragOver: (e) => {
                    if (disabled)
                        return;
                    e.preventDefault();
                    setDragging(true);
                }, onDragLeave: () => setDragging(false), onDrop: (e) => {
                    e.preventDefault();
                    setDragging(false);
                    if (!disabled)
                        void take(e.dataTransfer.files[0]);
                }, className: `flex h-[84px] w-[132px] shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted/40 ${dragging ? 'border-primary bg-primary/5' : ''} ${disabled ? '' : 'cursor-pointer hover:bg-muted'}`, children: [block.src ? ((0, jsx_runtime_1.jsx)("img", { src: (0, yahooArticles_1.yahooImageUrl)(block.src), alt: block.alt, className: "max-h-full max-w-full object-contain" })) : ((0, jsx_runtime_1.jsxs)("span", { className: "flex flex-col items-center gap-1 px-1 text-center text-[11px] text-muted-foreground", children: [(0, jsx_runtime_1.jsx)(lucide_react_1.ImagePlus, { className: "size-4" }), uploading ? t('components.YahooArticles.uploading') : t('components.YahooArticles.chooseImage')] })), (0, jsx_runtime_1.jsx)("input", { type: "file", accept: IMAGE_TYPES.join(','), disabled: disabled || uploading, className: "hidden", onChange: (e) => void take(e.target.files?.[0]) })] }), (0, jsx_runtime_1.jsxs)("div", { className: "flex min-w-0 flex-1 flex-col gap-1.5", children: [(0, jsx_runtime_1.jsx)("input", { ref: altRef, value: block.alt, disabled: disabled, onChange: (e) => onChange({ ...block, alt: e.target.value }), placeholder: t('components.YahooArticles.altPlaceholder'), className: "h-[30px] w-full rounded-md border bg-background px-2 text-[12.5px] disabled:bg-muted/40" }), (0, jsx_runtime_1.jsx)("span", { className: "text-[11px] text-muted-foreground", children: block.src ? `${block.width} × ${block.height}px · ${t('components.YahooArticles.replaceImage')}` : t('components.YahooArticles.imageHint') }), error && (0, jsx_runtime_1.jsx)("span", { className: "text-[11px] text-destructive", children: error })] })] }));
};
const BlockListEditor = ({ blocks, onChange, onFocusBlock, disabled, emptyHint, hint, }) => {
    const { t } = (0, react_i18next_1.useTranslation)();
    const upload = useImageUpload();
    const inputs = (0, react_1.useRef)(new Map());
    // Where the caret goes after a split, merge or paste (set before the re-render).
    const [focus, setFocus] = (0, react_1.useState)(null);
    const [removed, setRemoved] = (0, react_1.useState)(null);
    const [pasteError, setPasteError] = (0, react_1.useState)(null);
    const [pending, setPending] = (0, react_1.useState)(new Set());
    // Excel cells just pasted: the analyst picks an image or text.
    const [tableChoice, setTableChoice] = (0, react_1.useState)(null);
    (0, react_1.useEffect)(() => {
        if (!focus)
            return;
        const el = inputs.current.get(focus.id);
        if (el) {
            el.focus();
            const at = focus.at === 'start' ? 0 : el.value.length;
            el.setSelectionRange(at, at);
        }
        setFocus(null);
    }, [focus, blocks]);
    // A deleted block can be brought back until the next deletion.
    (0, react_1.useEffect)(() => {
        if (!removed)
            return;
        const timer = setTimeout(() => setRemoved(null), 10_000);
        return () => clearTimeout(timer);
    }, [removed]);
    const set = (index, block) => onChange(blocks.map((b, i) => (i === index ? block : b)));
    const insertAt = (index, added) => onChange([...blocks.slice(0, index), ...added, ...blocks.slice(index)]);
    const remove = (index) => {
        const block = blocks[index];
        const hasContent = block.type === 'Img' ? Boolean(block.src || block.alt) : block.text.trim() !== '';
        if (hasContent)
            setRemoved({ block, index });
        onChange(blocks.filter((_, i) => i !== index));
    };
    // An image block goes in after `index` at once and fills in when the upload is done.
    const insertImage = (index, image, kind = 'figure') => {
        setPasteError(null);
        const placeholder = (0, editorUtils_1.emptyBlock)('Img');
        const at = index + 1;
        onChange((prev) => [...prev.slice(0, at), placeholder, ...prev.slice(at)]);
        setPending((prev) => new Set(prev).add(placeholder.id));
        const done = () => setPending((prev) => {
            const next = new Set(prev);
            next.delete(placeholder.id);
            return next;
        });
        image
            .then(upload)
            .then((uploaded) => {
            onChange((prev) => prev.map((b) => b.id === placeholder.id ? { ...placeholder, ...(0, tableImage_1.displaySize)(uploaded, kind), alt: defaultAlt(prev, kind) } : b));
        })
            .catch((err) => {
            setPasteError(err.message);
            onChange((prev) => prev.filter((b) => b.id !== placeholder.id));
        })
            .finally(done);
    };
    // Several lines pasted at once (Word, a memo, Excel rows as text) become several blocks.
    const pasteText = (index, text) => {
        const pasted = (0, editorUtils_1.blocksFromPaste)(text);
        if (pasted.length === 0)
            return;
        const current = blocks[index];
        const currentEmpty = current !== undefined && current.type !== 'Img' && current.text.trim() === '';
        onChange(currentEmpty
            ? [...blocks.slice(0, index), ...pasted, ...blocks.slice(index + 1)]
            : [...blocks.slice(0, index + 1), ...pasted, ...blocks.slice(index + 1)]);
        setFocus({ id: pasted[pasted.length - 1].id, at: 'end' });
    };
    const onPaste = (index) => (event) => {
        const text = event.clipboardData.getData('text/plain');
        // Cells from Excel: ask first, since Excel also puts its own picture of them on the clipboard.
        const rows = (0, tableImage_1.excelTable)(text);
        if (rows) {
            event.preventDefault();
            setTableChoice({ index, rows, text });
            return;
        }
        // A screenshot (a table or a chart) pasted straight in becomes an image block.
        const image = Array.from(event.clipboardData.files).find((file) => IMAGE_TYPES.includes(file.type));
        if (image) {
            event.preventDefault();
            insertImage(index, Promise.resolve(image));
            return;
        }
        if (!/\n/.test(text.trim()))
            return;
        event.preventDefault();
        pasteText(index, text);
    };
    // An image file dropped on a text block goes in after it (instead of the browser opening the file).
    const onDrop = (index) => (event) => {
        const image = Array.from(event.dataTransfer.files).find((file) => file.type.startsWith('image/'));
        if (!image)
            return;
        event.preventDefault();
        insertImage(index, Promise.resolve(image));
    };
    const onKeyDown = (index) => (event) => {
        // Never while the IME is converting: that Enter confirms the Japanese text.
        if (event.nativeEvent.isComposing || event.keyCode === 229)
            return;
        const block = blocks[index];
        if (block.type === 'Img')
            return;
        const el = event.currentTarget;
        if (event.key === 'Enter' && !event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey) {
            // Enter starts a new paragraph at the caret; Shift+Enter is a line break inside it.
            event.preventDefault();
            const [before, after] = (0, editorUtils_1.splitBlock)(block, el.selectionStart);
            onChange([...blocks.slice(0, index), before, after, ...blocks.slice(index + 1)]);
            setFocus({ id: after.id, at: 'start' });
        }
        else if (event.key === 'Backspace' && el.selectionStart === 0 && el.selectionEnd === 0 && index > 0) {
            const previous = blocks[index - 1];
            if (block.text === '') {
                // An empty block goes away and the caret returns to the one above.
                event.preventDefault();
                onChange(blocks.filter((_, i) => i !== index));
                setFocus({ id: previous.id, at: 'end' });
            }
            else if (previous.type !== 'Img' && block.type === previous.type) {
                // At the start of a paragraph, Backspace joins it to the paragraph above.
                event.preventDefault();
                const joined = { ...previous, text: `${previous.text}${block.text}` };
                onChange([...blocks.slice(0, index - 1), joined, ...blocks.slice(index + 1)]);
                setFocus({ id: previous.id, at: 'end' });
            }
        }
    };
    return ((0, jsx_runtime_1.jsxs)("div", { className: "flex flex-col gap-1.5", children: [blocks.length === 0 && (0, jsx_runtime_1.jsx)("p", { className: "rounded-md border border-dashed px-3 py-3 text-center text-[12px] text-muted-foreground", children: emptyHint }), blocks.map((block, index) => {
                const issue = pending.has(block.id) ? null : blockIssue(block);
                return ((0, jsx_runtime_1.jsxs)("div", { "data-issue": issue ?? undefined, onFocus: () => onFocusBlock?.(index), className: "group scroll-mt-24 rounded-md border border-transparent px-1.5 py-1 hover:border-border focus-within:border-primary/40 focus-within:bg-card", children: [(0, jsx_runtime_1.jsxs)("div", { className: "flex items-center gap-1 opacity-70 group-focus-within:opacity-100 group-hover:opacity-100", children: [(0, jsx_runtime_1.jsx)("select", { value: block.type, disabled: disabled, onChange: (e) => set(index, (0, editorUtils_1.convertBlock)(block, e.target.value)), "aria-label": t('components.YahooArticles.blockType'), className: controlClass, children: BLOCK_TYPES.map((type) => ((0, jsx_runtime_1.jsx)("option", { value: type, children: t(`components.YahooArticles.blockTypes.${type}`) }, type))) }), block.type === 'Header' && ((0, jsx_runtime_1.jsx)("select", { value: LEVELS.includes(block.level) ? block.level : 'h4', disabled: disabled, onChange: (e) => set(index, { ...block, level: e.target.value }), "aria-label": t('components.YahooArticles.headingLevel'), className: controlClass, children: LEVELS.map((level) => ((0, jsx_runtime_1.jsx)("option", { value: level, children: t(`components.YahooArticles.levels.${level}`) }, level))) })), (0, jsx_runtime_1.jsxs)("span", { className: "ml-auto flex items-center gap-0.5 opacity-60 group-focus-within:opacity-100 group-hover:opacity-100", children: [(0, jsx_runtime_1.jsx)("button", { type: "button", disabled: disabled || index === 0, onClick: () => onChange((0, editorUtils_1.move)(blocks, index, index - 1)), className: iconButton, "aria-label": t('components.YahooArticles.moveUp'), children: (0, jsx_runtime_1.jsx)(lucide_react_1.ArrowUp, { className: "size-3.5" }) }), (0, jsx_runtime_1.jsx)("button", { type: "button", disabled: disabled || index === blocks.length - 1, onClick: () => onChange((0, editorUtils_1.move)(blocks, index, index + 1)), className: iconButton, "aria-label": t('components.YahooArticles.moveDown'), children: (0, jsx_runtime_1.jsx)(lucide_react_1.ArrowDown, { className: "size-3.5" }) }), (0, jsx_runtime_1.jsx)("button", { type: "button", disabled: disabled, onClick: () => remove(index), className: `${iconButton} hover:text-destructive`, "aria-label": t('components.YahooArticles.remove'), children: (0, jsx_runtime_1.jsx)(lucide_react_1.Trash2, { className: "size-3.5" }) })] })] }), block.type === 'Img' ? ((0, jsx_runtime_1.jsx)(ImageBlock, { block: block, disabled: disabled, pending: pending.has(block.id), defaultCaption: defaultAlt(blocks, 'figure'), altRef: (el) => {
                                if (el)
                                    inputs.current.set(block.id, el);
                                else
                                    inputs.current.delete(block.id);
                            }, onChange: (next) => set(index, next) })) : ((0, jsx_runtime_1.jsx)(AutoGrow, { value: block.text, disabled: disabled, inputRef: (el) => {
                                if (el)
                                    inputs.current.set(block.id, el);
                                else
                                    inputs.current.delete(block.id);
                            }, onChange: (text) => set(index, { ...block, text }), onPaste: onPaste(index), onKeyDown: onKeyDown(index), onDrop: onDrop(index), placeholder: t(`components.YahooArticles.placeholders.${block.type}`), className: block.type === 'Header' ? 'text-[14px] font-medium' : block.type === 'Supplement' ? 'text-[13px] font-semibold' : 'text-[13px]' })), issue && (0, jsx_runtime_1.jsx)("p", { className: "mt-1 text-[11px] text-warning-fg", children: t(`components.YahooArticles.issues.${issue}`) }), tableChoice?.index === index && ((0, jsx_runtime_1.jsxs)("div", { className: "mt-1.5 flex flex-wrap items-center gap-1.5 rounded-md border border-primary/30 bg-primary/5 px-2.5 py-1.5 text-[12px]", children: [(0, jsx_runtime_1.jsx)("span", { className: "mr-1", children: t('components.YahooArticles.table.pasted', { rows: tableChoice.rows.length, cols: tableChoice.rows[0].length }) }), (0, jsx_runtime_1.jsx)("button", { type: "button", disabled: !(0, tableImage_1.tableFitsImage)(tableChoice.rows), title: (0, tableImage_1.tableFitsImage)(tableChoice.rows) ? undefined : t('components.YahooArticles.table.tooLarge'), onClick: () => {
                                        insertImage(index, (0, tableImage_1.renderTableImage)(tableChoice.rows), 'table');
                                        setTableChoice(null);
                                    }, className: "h-[26px] rounded-md bg-primary px-2.5 text-[12px] font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-40", children: t('components.YahooArticles.table.asImage') }), (0, jsx_runtime_1.jsx)("button", { type: "button", onClick: () => {
                                        pasteText(index, tableChoice.text);
                                        setTableChoice(null);
                                    }, className: "h-[26px] rounded-md border bg-card px-2.5 text-[12px] hover:bg-muted", children: t('components.YahooArticles.table.asText') }), (0, jsx_runtime_1.jsx)("button", { type: "button", onClick: () => setTableChoice(null), className: "h-[26px] rounded-md px-2 text-[12px] text-muted-foreground hover:bg-muted", children: t('components.YahooArticles.table.cancel') })] })), !disabled && ((0, jsx_runtime_1.jsx)("div", { className: "mt-1.5 hidden gap-1 group-focus-within:flex", children: BLOCK_TYPES.map((type) => ((0, jsx_runtime_1.jsxs)("button", { type: "button", onClick: () => {
                                    const added = (0, editorUtils_1.emptyBlock)(type);
                                    insertAt(index + 1, [added]);
                                    setFocus({ id: added.id, at: 'start' });
                                }, className: "h-[22px] rounded border border-dashed px-1.5 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground", children: ["+ ", t(`components.YahooArticles.blockTypes.${type}`)] }, type))) }))] }, block.id));
            }), removed && ((0, jsx_runtime_1.jsxs)("div", { className: "flex items-center gap-2 rounded-md bg-muted px-3 py-1.5 text-[12px]", children: [(0, jsx_runtime_1.jsx)("span", { className: "min-w-0 flex-1 truncate", children: t('components.YahooArticles.removedBlock', {
                            text: removed.block.type === 'Img' ? removed.block.alt || t('components.YahooArticles.blockTypes.Img') : removed.block.text,
                        }) }), (0, jsx_runtime_1.jsx)("button", { type: "button", onClick: () => {
                            insertAt(Math.min(removed.index, blocks.length), [removed.block]);
                            setRemoved(null);
                        }, className: "h-[24px] rounded-md border bg-card px-2 text-[11.5px] hover:bg-muted", children: t('components.YahooArticles.undo') })] })), pasteError && (0, jsx_runtime_1.jsx)("p", { className: "text-[11.5px] text-destructive", children: pasteError }), !disabled && ((0, jsx_runtime_1.jsxs)("div", { className: "flex flex-wrap gap-1.5", children: [BLOCK_TYPES.map((type) => ((0, jsx_runtime_1.jsxs)("button", { type: "button", onClick: () => {
                            const added = (0, editorUtils_1.emptyBlock)(type);
                            onChange([...blocks, added]);
                            setFocus({ id: added.id, at: 'start' });
                        }, className: "h-[28px] rounded-md border border-dashed px-2.5 text-[12px] text-muted-foreground hover:bg-muted hover:text-foreground", children: ["+ ", t(`components.YahooArticles.blockTypes.${type}`)] }, type))), hint && (0, jsx_runtime_1.jsx)("span", { className: "self-center text-[11px] text-muted-foreground", children: hint })] }))] }));
};
exports.BlockListEditor = BlockListEditor;
